import { Component, computed, DestroyRef, input, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { GoogleAnalyticsService } from '@app/core/analytics/google-analytics.service';
import { Application, Building, Customer, Document, Documents, DocumentType, Mortgage, Task } from '@app/core/stingray-api/models';
import { DocumentsService } from '@app/core/stingray-api/services';
import { BlobDownloadService } from '@app/core/blob-download.service';
import { FileDialogComponent, FileDialogData } from '@app/shared/files/file-dialog/file-dialog.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { FileApplicationDialogComponent, FileApplicationDialogData } from '../file-application-dialog/file-application-dialog.component';
import { FilesService } from '../files.service';
import { ShellLoaderService } from '@app/shell/shell-loader.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FileRowComponent } from '../file-row/file-row.component';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatFormField } from '@angular/material/form-field';
import { AsyncPipe } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent, MatCardFooter } from '@angular/material/card';

@Component({
    selector: 'unite-files-tab',
    templateUrl: './files-tab.component.html',
    styleUrls: ['./files-tab.component.scss'],
    imports: [MatCard, MatCardHeader, MatCardTitle, MatCheckbox, MatFormField, MatInput, ReactiveFormsModule, MatAutocompleteTrigger, MatAutocomplete, MatOption, TranslateModule, MatSelect, MatCardContent, MatSelectionList, MatListOption, FileRowComponent, MatProgressSpinner, MatCardFooter, MatButton, MatIcon, MatTooltip, MatPaginator, AsyncPipe]
})
export class FilesTabComponent implements OnInit {

  private destroyRef = inject(DestroyRef);
  documentTypes = input<DocumentType[]>();
  icon = input<string>();
  title = input<string>();
  customer = input<Customer>();
  objectSearch = input(false);
  type = input<'Building' | 'Mortgage' | 'Customer' | 'Other' | 'Application' | 'All' | 'Task'>();
  loadDocument = input<Subject<'Building' | 'Mortgage' | 'Customer' | 'Other' | 'Application' | 'All' | 'Task'>>();
  object = input<Building | Mortgage | Application | Task>(null);
  customers = input<Customer[]>();
  applications = input<Application[]>();
  mortgages = input<Mortgage[]>();
  buildings = input<Building[]>();

  documentTypeFormControl = new FormControl();
  documentTypesFilter = computed(() =>
    this.documentTypes()?.filter((documentType: DocumentType) => {
      if (this.type() === 'All' || this.type() === 'Application') { return true; }
      if (this.filesService.isCustomer(documentType.associationType) && this.type() === 'Customer') { return true; }
      if (documentType.associationType === this.type()) { return true; }
      return false;
    }) ?? []
  );
  documentTypesFiltered: Observable<DocumentType[]>;

  applicationFormControl = new FormControl();
  applicationsFiltered: Observable<Application[]>;

  mortgageFormControl = new FormControl();
  mortgagesFiltered: Observable<Mortgage[]>;

  buildingFormControl = new FormControl();
  buildingsFiltered: Observable<Building[]>;

  uploadYearFormControl = new FormControl();


  @ViewChild(MatPaginator, { static: true }) filesPaginator: MatPaginator;
  @ViewChild(MatSelectionList, { static: true }) matSelectionList: MatSelectionList;

  readonly SS_PAGINATOR = 'unite-files-tab-paginator';

  documents: Document[] = [];
  documentsLoading = true;
  documentsLength = 0;

  years: number[] = [];

  private _selectedObject: Building | Mortgage | Application | Task = null;

  constructor(private matDialog: MatDialog,
    private filesService: FilesService,
    private shellLoaderService: ShellLoaderService,
    private blobDownloadService: BlobDownloadService,
    private documentsService: DocumentsService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {
    const currentYear = new Date().getFullYear();
    
    for (let i = 0; i < 4; i++) {
      this.years.push(currentYear - i);
    }
  }

  ngOnInit() {
    this._selectedObject = this.object();

    this.initFilters();

    // Cached page settings
    const paginator: PageEvent = JSON.parse(localStorage.getItem(this.SS_PAGINATOR));
    if (paginator) {
      this.filesPaginator.pageIndex = paginator.pageIndex;
      this.filesPaginator.pageSize = paginator.pageSize;
      this.filesPaginator.length = paginator.length;
    } else {
      this.filesPaginator.pageSize = 15;
    }

    this.loadDocument()?.subscribe((value) => {
      if (value === this.type()) {
        this.loadDocuments();
      }
    });
  }

  public displayDocumentType(documentType: DocumentType): string {
    return documentType ? documentType.id + '. ' + documentType.name : '';
  }

  public displayBuilding(building: Building): string {
    return building ? (building.address.addressLine1 + ', ' + building.address.city) : '';
  }

  public displayMortgage(mortgage: Mortgage): string {
    return mortgage ? ((mortgage.number || '...') + ' - ' + mortgage.lender.name) : '';
  }

  public displayApplication(application: Application): string {
    return application ? (application.name + ' - ' + application.mortgage.lender.name) : '';
  }


  getFileExtension(fileName: string): string {
    return this.filesService.getFileExtension(fileName);
  }

  getLinkText(document: Document): string {
    return this.filesService.getLinkText(document, false);
  }

  public showEdit(document: Document) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = false;
    dialogConfig.width = '520px';
    dialogConfig.data = <FileDialogData>{
      document: document,
      documentTypes: this.documentTypes(),
      customers: this.customers(),
      applications: this.applications(),
      mortgages: this.mortgages(),
      buildings: this.buildings()
    };
    this.matDialog.open(FileDialogComponent, dialogConfig).afterClosed().subscribe((updatedDocument?: Document) => {

      if (updatedDocument === null) {
        this.documents.splice(this.documents.indexOf(document), 1);
        this.customer().documentsCount -= 1;
      }
      if (this.type() !== 'All') {
        if (updatedDocument && updatedDocument.documentType) {
          if (document.documentType == null || document.documentType.associationType !== updatedDocument.documentType.associationType) {
            this.documents.splice(this.documents.indexOf(document), 1);
          } else {
            document = updatedDocument;
          }
        }
      }
    });
  }

  public pageChange(pageEvent: PageEvent) {
    this.loadDocuments(pageEvent.pageIndex, pageEvent.pageSize);
    localStorage.setItem(this.SS_PAGINATOR, JSON.stringify(pageEvent));
  }

  public downloadDocument(document: Document) {
    this.shellLoaderService.setRequestLoader(true);
    this.googleAnalyticsService.trackEvent('document', 'download', 'tab-download');
    const path = DocumentsService.DownloadDocumentPath.replace('{id}', document.id);
    this.blobDownloadService.download(path, undefined, document.originalFileName)
      .pipe(finalize(() => this.shellLoaderService.setRequestLoader(false))).subscribe();
  }

  public openDocument(document: Document) {
    this.shellLoaderService.setRequestLoader(true);
    this.googleAnalyticsService.trackEvent('document', 'download', 'tab-open');
    const path = DocumentsService.DownloadDocumentPath.replace('{id}', document.id);
    this.blobDownloadService.open(path, document.originalFileName)
      .pipe(finalize(() => this.shellLoaderService.setRequestLoader(false))).subscribe();
  }

  downloadSelectedDocuments() {
    this.shellLoaderService.setRequestLoader(true);
    this.googleAnalyticsService.trackEvent('document', 'download bulk', 'tab-download');
    const selected = this.getSelectedDocuments();
    const params: { [key: string]: string[] } = { documentIds: selected.map(d => d.id) };
    this.blobDownloadService.download(DocumentsService.ZipDocumentsPath, params, 'file.zip')
      .pipe(finalize(() => this.shellLoaderService.setRequestLoader(false))).subscribe();
  }

  getSelectedDocuments(): Document[] {
    return this.matSelectionList.selectedOptions.selected.map((matListOption: MatListOption): Document => {
      return <Document>matListOption.value;
    });
  }

  linkSelectedDocumentsToApplication() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = false;
    dialogConfig.width = '520px';
    dialogConfig.data = <FileApplicationDialogData>{
      documents: this.getSelectedDocuments(),
      applications: this.applications(),
    };
    this.matDialog.open(FileApplicationDialogComponent, dialogConfig).afterClosed().subscribe((updatedDocuments?: Document[]) => {

    });
  }


  isAllSelected() {
    const numSelected = this.matSelectionList.selectedOptions.selected.length;
    const numRows = this.documents.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.matSelectionList.deselectAll() :
      this.matSelectionList.selectAll();
  }


  private loadDocuments(page: number = 0, count: number = 15) {
    this.documents = [];
    this.documentsLoading = true;
    if (this.type() === 'All') {
      this.documentsService.getDocuments({
        page: page,
        count: count,
        customerId: this.customer().id,
        uploadYear: this.uploadYearFormControl.value,
        documentTypeId: this.documentTypeFormControl.value ? this.documentTypeFormControl.value.id : null,
      }).pipe(finalize(() => {
        this.documentsLoading = false;
      })).subscribe((documents: Documents) => {
        this.documents = documents.items;
        this.documentsLength = documents.count;
      });
    } else {
      this.documentsService.getDocuments({
        page: page,
        count: count,
        customerId: this.customer().id,
        uploadYear: this.uploadYearFormControl.value,
        documentTypeId: this.documentTypeFormControl.value ? this.documentTypeFormControl.value.id : null,
        hasDocumentType: this.type() === 'Task' ? null : this.type() !== 'Other',
        associatedWithBuilding: this.type() === 'Building',
        associatedWithMortgage: this.type() === 'Mortgage',
        associatedWithApplication: this.type() === 'Application',
        associatedWithCustomer: this.type() === 'Customer',
        buildingId: this.type() === 'Building' && this._selectedObject ? this._selectedObject.id : null,
        mortgageId: this.type() === 'Mortgage' && this._selectedObject ? this._selectedObject.id : null,
        applicationId: this.type() === 'Application' && this._selectedObject ? this._selectedObject.id : null,
        taskId: this.type() === 'Task' && this._selectedObject ? this._selectedObject.id : null,
      }).pipe(finalize(() => {
        this.documentsLoading = false;
      })).subscribe((documents: Documents) => {
        this.documents = documents.items;
        this.documentsLength = documents.count;
      });
    }
  }

  private initFilters(): void {
    this.uploadYearFormControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.loadDocuments();
    });

    this.documentTypesFiltered = this.documentTypeFormControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((value: string | DocumentType) => {
        if (value === '' || value === null) {
          this.loadDocuments();
          return this.documentTypesFilter();
        }
        if (typeof value === 'string') {
          return this.filterDocumentType(value);
        } else {
          this.loadDocuments();
          return this.filterDocumentType(value.name);
        }
      })
    );

    this.buildingsFiltered = this.buildingFormControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((value: string | Building) => {
        if (value === '' || value === null) {
          this._selectedObject = null;
          this.loadDocuments();
          return null;
        }
        if (typeof value === 'string') {
          return this.filterBuilding(value);
        } else {
          this._selectedObject = value;
          this.loadDocuments();
          return this.filterBuilding(value.address.addressLine1);
        }
      })
    );

    this.mortgagesFiltered = this.mortgageFormControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((value: string | Mortgage) => {
        if (value === '' || value === null) {
          this._selectedObject = null;
          this.loadDocuments();
          return null;
        }
        if (typeof value === 'string') {
          return this.filterMortgage(value);
        } else {
          this._selectedObject = value;
          this.loadDocuments();
          return this.filterMortgage(value.number);
        }
      })
    );

    this.applicationsFiltered = this.applicationFormControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((value: string | Application) => {
        if (value === '' || value === null) {
          this._selectedObject = null;
          this.loadDocuments();
          return null;
        }
        if (typeof value === 'string') {
          return this.filterApplication(value);
        } else {
          this._selectedObject = value;
          this.loadDocuments();
          return this.filterApplication(value.name);
        }
      })
    );
  }

  private filterDocumentType(value: string): DocumentType[] {
    const filterValue = value.toLowerCase();
    return this.documentTypesFilter().filter((documentType: DocumentType) => {
      return documentType.name.toLowerCase().includes(filterValue) || documentType.id.includes(value);
    });
  }

  private filterBuilding(value: string): Building[] {
    const filterValue = value.toLowerCase();
    return this.buildings().filter((building: Building) => {
      return building.address.city.toLowerCase().includes(filterValue) ||
        building.address.postalCode.toLowerCase().includes(filterValue) ||
        building.address.addressLine1.toLowerCase().includes(filterValue);
    });
  }

  private filterMortgage(value: string): Mortgage[] {
    const filterValue = value.toLowerCase();
    return this.mortgages().filter((mortgage: Mortgage) => {
      return mortgage.number.toLowerCase().includes(filterValue) ||
        mortgage.lender.name.toLowerCase().includes(filterValue);
    });
  }

  private filterApplication(value: string): Application[] {
    const filterValue = value.toLowerCase();
    return this.applications().filter((application: Application) => {
      return application.name.toLowerCase().includes(filterValue) ||
        application.mortgage.lender.name.toLowerCase().includes(filterValue);
    });
  }
}
